import { ChangeDetectorRef, Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Router, Event, NavigationEnd} from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { ConnectionsService } from '../../services/connectionsWeb3/connections.service';
import { KycService } from '../../services/kyc.service';
import { Subject, takeUntil } from 'rxjs';
import truncateEthAddress from 'truncate-eth-address';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ModalLoginComponent } from '../modal-login/modal-login.component';
import { ModalRegisterComponent } from '../modal-register/modal-register.component';

// Interfaces para tipado
interface UserAuth {
  uid: string;
  email?: string;
  displayName?: string;
}

interface UserData {
  displayName?: string;
  hideBlockchain?: boolean;
  gender?: string;
}

interface KycStatus {
  success: boolean;
  revision: boolean;
  aproved: boolean;
}

@Component({
  selector: 'app-navbar-user',
  templateUrl: './navbar-user.component.html',
  styleUrls: ['./navbar-user.component.css'],
  standalone: true,
  imports: [CommonModule]
})
export class NavbarUserComponent implements OnInit, OnDestroy {
  path: string | null = null;
  userAuth: UserAuth | null = null;
  loggedIn: boolean = false;
  acreditado: boolean = false;
  revision: boolean = false;
  addressUser: string | null = null;
  addressUserTruncate: string | null = null;
  nameUser: string = '';
  userGender: string | null = null; // Propiedad para el género del usuario
  hideBlockchain: boolean = true;

  // ✅ KISS Pattern: Simple subscription cleanup
  private destroy$ = new Subject<void>();

  constructor(
    private authSrv: AuthService,
    private afsSrv: AngularFirestore,
    private connectionsWeb3: ConnectionsService,
    private router: Router,
    private kycSrv: KycService,
    private crf: ChangeDetectorRef,
    private modalService: NgbModal
  ) {}

  connect() {
    this.connectionsWeb3.connect();
  }

  openLoginForm() {
    const modalRef = this.modalService.open(ModalLoginComponent);
    modalRef.closed.subscribe(() => {
    });
  }

  openRegisterForm() {
    const modalRef = this.modalService.open(ModalRegisterComponent);
    modalRef.closed.subscribe(() => {
    });
  }

  ngOnInit(): void {
    this.tryAutoConnectWallet();

    this.authSrv.userAuth
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (res: UserAuth | null) => {
      this.userAuth = res;

      if (this.userAuth === null || this.nameUser !== '') {
        return;
      }
      this.afsSrv
        .collection('users')
        .doc(this.userAuth.uid)
        .valueChanges()
        .pipe(takeUntil(this.destroy$))
        .subscribe((res: UserData) => {
          res !== undefined && res.displayName !== undefined
            ? (this.nameUser = res.displayName)
            : (this.nameUser = '');
          this.hideBlockchain = res !== undefined && res.hideBlockchain !== undefined ? res.hideBlockchain : true;

          // Obtener el género del usuario si existe en Firestore
          if (res !== undefined && res.gender !== undefined) {
            this.userGender = res.gender; // Asignar género si está disponible
          } else {
            this.userGender = '@'; // Si no hay género, asignar "@"
          }

          if (this.hideBlockchain === false) {
            this.connectionsWeb3.addressUser
              .pipe(takeUntil(this.destroy$))
              .subscribe((res: string) => {
              res !== '' ? (this.addressUser = res) : (this.addressUser = null);

              if (this.addressUser !== null) {
                this.addressUserTruncate = truncateEthAddress(this.addressUser);
              } else {
                this.addressUserTruncate = null;
              }

              this.crf.detectChanges();
            });
          }
        });
    });

    this.authSrv.loggedIn
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: boolean) => {
      this.loggedIn = res;
    });

    this.kycSrv.kycSuccess
      .pipe(takeUntil(this.destroy$))
      .subscribe(
      (res: KycStatus) => {
        this.acreditado = res.success;
        this.revision = res.revision;
        this.crf.detectChanges();
      }
    );

    this.router.events
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: Event) => {
      if (event instanceof NavigationEnd) {
        this.path = event.url;
        console.log(this.path);
      }
    });
  }

  /**
   * Intenta conectar automáticamente a la wallet si ya está autorizada
   */
  async tryAutoConnectWallet(): Promise<void> {
    // Verificar si ethereum está disponible (MetaMask instalado)
    if (typeof window.ethereum !== 'undefined') {
      try {
        // Usar autoConnect en lugar de connect para evitar prompt
        const connected = await this.connectionsWeb3.autoConnect();
        console.log('Conexión automática:', connected ? 'exitosa' : 'fallida');
      } catch (error) {
        console.error('Error al verificar cuentas autorizadas:', error);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
